import { Text } from "@clipboard-health/ui-components";
import { Box, Button, Divider, Slider, Stack } from "@mui/material";
import { type ReactElement, useState } from "react";

import { RootPaths } from "../../App/paths";
import { AppBarHeader, BackButtonLink, PageWithHeader, useToast } from "../../lib";
import { useScreenBrightness } from "../../lib/utils";

/**
 * Debug page for testing screen brightness functionality
 * Allows manual control of screen brightness levels for testing purposes
 */
export function ScreenBrightnessDebugPage(): ReactElement {
  const { showErrorToast, showInfoToast } = useToast();
  const [brightnessValue, setBrightnessValue] = useState(0.5);
  const [brightnessEnabled, setBrightnessEnabled] = useState(false);

  // Use the screen brightness hook
  useScreenBrightness(brightnessValue, brightnessEnabled, (error, operation) => {
    if (operation === "set") {
      showErrorToast(`Failed to set brightness: ${String(error)}`);
    } else if (operation === "restore") {
      showErrorToast(`Failed to restore brightness: ${String(error)}`);
    }
  });

  return (
    <PageWithHeader
      appBarHeader={
        <AppBarHeader
          title="Screen Brightness"
          leftCta={<BackButtonLink defaultBackTo={`${RootPaths.APP_V2_HOME}/debug`} />}
        />
      }
      containerVariant="without-margin"
    >
      <Stack spacing={3} sx={{ padding: 2 }}>
        <Box>
          <Text variant="body2" sx={{ textAlign: "center" }}>
            Test screen brightness control functionality. Toggle to enable brightness control, then
            adjust the slider or use preset buttons.
          </Text>
        </Box>

        <Divider />

        <Box>
          <Button
            fullWidth
            variant={brightnessEnabled ? "contained" : "outlined"}
            color={brightnessEnabled ? "success" : "primary"}
            onClick={() => {
              setBrightnessEnabled(!brightnessEnabled);
              if (brightnessEnabled) {
                showInfoToast("Screen brightness control disabled - brightness will restore");
              } else {
                showInfoToast("Screen brightness control enabled");
              }
            }}
          >
            {brightnessEnabled ? "Disable Brightness Control" : "Enable Brightness Control"}
          </Button>
        </Box>

        <Box>
          <Text variant="body2" sx={{ mb: 2, textAlign: "center" }}>
            Brightness Level: {Math.round(brightnessValue * 100)}%
          </Text>
          <Slider
            value={brightnessValue}
            min={0}
            max={1}
            step={0.1}
            disabled={!brightnessEnabled}
            marks={[
              { value: 0, label: "0%" },
              { value: 0.5, label: "50%" },
              { value: 1, label: "100%" },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            onChange={(_event, newValue) => {
              setBrightnessValue(newValue as number);
            }}
          />
        </Box>

        <Box>
          <Text variant="body2" sx={{ mb: 1, textAlign: "center" }}>
            Quick Presets:
          </Text>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              disabled={!brightnessEnabled}
              sx={{ flex: 1 }}
              onClick={() => {
                setBrightnessValue(0.1);
              }}
            >
              Dim (10%)
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!brightnessEnabled}
              sx={{ flex: 1 }}
              onClick={() => {
                setBrightnessValue(0.5);
              }}
            >
              Medium (50%)
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={!brightnessEnabled}
              sx={{ flex: 1 }}
              onClick={() => {
                setBrightnessValue(1);
              }}
            >
              Max (100%)
            </Button>
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Text variant="body2" sx={{ textAlign: "center", fontStyle: "italic" }}>
            💡 Note: This functionality only works on native mobile devices (iOS/Android). On web
            browsers, you&apos;ll see toast notifications but brightness won&apos;t change.
          </Text>
        </Box>
      </Stack>
    </PageWithHeader>
  );
}
